import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { openWhatsApp } from '@/lib/whatsapp';
import { useStore } from '@/contexts/StoreContext';
import {
  Phone,
  MessageCircle,
  MapPin,
  Clock,
  Mail,
  Instagram,
  Facebook,
  Youtube,
} from 'lucide-react';

import googlePlay from '@/assets/google-play.png';
import appStore from '@/assets/app-store.png';
import { StoreFooterIcon } from '@/components/StoreFooterIcon';
import { hasAppStoreLink as hasResolvedAppStoreLink, resolveAppStoreUrl } from '@/lib/appStoreLinks';
import './styles.css';
import './footer.css';

interface FooterProps {
  config: any;
}

export const Footer = ({ config }: FooterProps) => {
  const { pathname } = useLocation();
  const { selectedStore } = useStore();
  const store = selectedStore || config.stores[0];
  const { colors } = useTheme();
  const buttonColor = colors.button;
  const hasGooglePlayLink = !!config.googlePlay?.url?.trim();
  const appStoreUrl = resolveAppStoreUrl(config.appStore);
  const hasAppStoreLink = hasResolvedAppStoreLink(config.appStore);

  const formatOpeningHours = () => {
    const hours = store.openingHours?.default;
    if (!hours) return [];

    const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    const formattedHours: { day: string; time: string }[] = [];

    const weekdayHours = weekDays.map((day) => hours[day]);
    const allWeekdaysSame = weekdayHours.every(
      (h, i, arr) => h?.from === arr[0]?.from && h?.to === arr[0]?.to
    );

    if (allWeekdaysSame && hours.monday?.from && hours.monday?.to) {
      formattedHours.push({
        day: 'Seg–Sex',
        time: `${hours.monday.from} – ${hours.monday.to}`,
      });
    }

    if (hours.saturday?.from && hours.saturday?.to) {
      formattedHours.push({
        day: 'Sáb',
        time: `${hours.saturday.from} – ${hours.saturday.to}`,
      });
    }

    if (hours.sunday?.from && hours.sunday?.to) {
      formattedHours.push({
        day: 'Dom',
        time: `${hours.sunday.from} – ${hours.sunday.to}`,
      });
    } else if (!hours.sunday?.from) {
      formattedHours.push({ day: 'Dom', time: 'Fechado' });
    }

    return formattedHours;
  };

  const openingHours = formatOpeningHours();
  const hasContactCta = !!(store.phone?.formatted || store.whatsapp?.formatted);

  const footerCta = (() => {
    if (pathname === '/contato') {
      return {
        variant: 'dual' as const,
        title: 'Prefere falar agora?',
        subtitle: 'Nossa equipe está pronta para atender você no WhatsApp ou telefone.',
      };
    }
    if (pathname === '/servicos') {
      return {
        variant: 'dual' as const,
        title: 'Precisa de ajuda com algum serviço?',
        subtitle: 'Fale com nossa equipe agora e receba atendimento personalizado.',
      };
    }
    if (pathname === '/favoritos') {
      return {
        variant: 'dual' as const,
        title: 'Gostou de algum veículo?',
        subtitle: 'Entre em contato para mais informações, agendar test-drive ou simular financiamento.',
      };
    }
    return {
      variant: 'default' as const,
      title: 'Tem alguma dúvida?',
      subtitle: 'Ligue agora.',
    };
  })();

  const socialLinks = [
    config.socialNetworks?.instagram && { icon: Instagram, href: config.socialNetworks.instagram, label: 'Instagram' },
    config.socialNetworks?.facebook && { icon: Facebook, href: config.socialNetworks.facebook, label: 'Facebook' },
    config.socialNetworks?.youtube && { icon: Youtube, href: config.socialNetworks.youtube, label: 'YouTube' },
  ].filter(Boolean) as { icon: typeof Instagram; href: string; label: string }[];

  const description =
    config.about?.split('\n')[0] ||
    config.slogan ||
    'Seminovos e zero KM com atendimento personalizado e as melhores condições do mercado.';

  return (
    <div className="layout-5 wl5-footer-wrap">
      <footer className="wl5-footer">
        {hasContactCta && (
          <div className="wl5-footer-cta-band">
            <div className="wl5-container">
              <div
                className={`wl5-footer-cta${footerCta.variant === 'dual' ? ' wl5-footer-cta--dual' : ''}`}
                style={{ backgroundColor: buttonColor }}
              >
                {footerCta.variant === 'dual' ? (
                  <>
                    <div className="wl5-footer-cta-copy">
                      <p className="wl5-footer-cta-text">{footerCta.title}</p>
                      <p className="wl5-footer-cta-subtext">{footerCta.subtitle}</p>
                    </div>
                    <div className="wl5-footer-cta-actions">
                      {store.whatsapp?.ios && (
                        <button
                          type="button"
                          className="wl5-footer-cta-btn wl5-footer-cta-btn--solid"
                          style={{ color: buttonColor }}
                          onClick={() => openWhatsApp(store.whatsapp?.ios)}
                        >
                          <MessageCircle className="h-5 w-5" />
                          WhatsApp
                        </button>
                      )}
                      {store.phone?.formatted && (
                        <a
                          href={`tel:${store.phone.ios || store.phone.formatted}`}
                          className="wl5-footer-cta-btn wl5-footer-cta-btn--ghost"
                        >
                          <Phone className="h-5 w-5" />
                          Ligar agora
                        </a>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="wl5-footer-cta-text">
                      {footerCta.title}<br />
                      {footerCta.subtitle}
                    </p>
                    <div className="wl5-footer-cta-action">
                      {store.phone?.formatted ? (
                        <a href={`tel:${store.phone.ios || store.phone.formatted}`} className="wl5-footer-cta-link">
                          <span className="wl5-footer-cta-icon" style={{ color: buttonColor }}>
                            <Phone className="h-6 w-6" />
                          </span>
                          {store.phone.formatted}
                        </a>
                      ) : (
                        <button
                          type="button"
                          className="wl5-footer-cta-link"
                          onClick={() => openWhatsApp(store.whatsapp?.ios)}
                        >
                          <span className="wl5-footer-cta-icon" style={{ color: buttonColor }}>
                            <MessageCircle className="h-6 w-6" />
                          </span>
                          WhatsApp
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="wl5-footer-body">
          <div className="wl5-container wl5-footer-main">
            <div className="wl5-footer-col wl5-footer-col--brand">
              <div className="wl5-footer-brand-stack">
                <StoreFooterIcon
                  config={config}
                  store={store}
                  alt={config.name}
                  className="h-14 w-auto max-w-[180px] object-contain"
                  wrapperClassName="wl5-footer-logo"
                />
                <h3 className="wl5-footer-brand-name">{config.name}</h3>
              </div>
              <p className="wl5-footer-desc">{description}</p>
            </div>

            <div className="wl5-footer-col">
              <h4 className="wl5-footer-heading">Contato &amp; legal</h4>
              <ul className="wl5-footer-links">
                <li>
                  <Link to="/politica-privacidade" className="wl5-footer-link">
                    Política de Privacidade
                  </Link>
                </li>
                <li>
                  <Link to="/termos-uso" className="wl5-footer-link">
                    Termos de Uso
                  </Link>
                </li>
                <li>
                  <Link to="/contato" className="wl5-footer-link">
                    Fale conosco
                  </Link>
                </li>
              </ul>
            </div>

            <div className="wl5-footer-col">
              {(hasGooglePlayLink || hasAppStoreLink) && (
                <>
                  <h4 className="wl5-footer-heading">Baixe o app</h4>
                  <div className="wl5-footer-app-badges">
                    {hasGooglePlayLink && (
                      <a href={config.googlePlay.url} target="_blank" rel="noopener noreferrer">
                        <img src={googlePlay} alt="Disponível no Google Play" width={130} height={40} />
                      </a>
                    )}
                    {hasAppStoreLink && (
                      <a href={appStoreUrl} target="_blank" rel="noopener noreferrer">
                        <img src={appStore} alt="Baixar na App Store" width={130} height={40} />
                      </a>
                    )}
                  </div>
                </>
              )}

              <h4 className={`wl5-footer-heading${hasGooglePlayLink || hasAppStoreLink ? ' wl5-footer-heading--spaced' : ''}`}>
                Onde estamos
              </h4>
              <ul className="wl5-footer-contact-list">
                {store?.place?.address && (
                  <li className="wl5-footer-contact-row">
                    <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{store.place.address}</span>
                  </li>
                )}
                {config.email && (
                  <li className="wl5-footer-contact-row">
                    <Mail className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <a href={`mailto:${config.email}`}>{config.email}</a>
                  </li>
                )}
                {openingHours.length > 0 && (
                  <li className="wl5-footer-contact-row">
                    <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span>{openingHours.map((h) => `${h.day} ${h.time}`).join(' · ')}</span>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        <div className="wl5-footer-bar">
          <div className="wl5-container wl5-footer-bar-inner">
            <p className="wl5-footer-copy">
              &copy; {new Date().getFullYear()} {config.name}. Todos os direitos reservados.
            </p>
            {socialLinks.length > 0 && (
              <div className="wl5-footer-social">
                <span className="wl5-footer-social-label">Siga-nos</span>
                <div className="wl5-footer-social-icons">
                  {socialLinks.map(({ icon: Icon, href, label }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="wl5-footer-social-link"
                      style={{ backgroundColor: buttonColor }}
                      aria-label={label}
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};
